import { EventRecurrence } from '@bp/bp-graphql-types';
import type { ProfileClaim } from '@bp/pim-auth-constants';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import i18n from 'core/i18n/config';
import { backendApi, type JoinParams } from './backendApi';
import { showErrorToast } from './showErrorToast';
import { showSuccessToast } from './showSuccessToast';

function getRecurrenceEventText(recurrence: EventRecurrence | null | undefined) {
  let text = '';

  if (recurrence === EventRecurrence.Daily) text = i18n.t('appointments.recurrences.daily');
  else if (recurrence === EventRecurrence.Monthly) text = i18n.t('appointments.recurrences.monthly');
  else if (recurrence === EventRecurrence.Weekly) text = i18n.t('appointments.recurrences.weekly');
  else if (recurrence === EventRecurrence.Workdays) text = i18n.t('appointments.recurrences.workdays');
  else if (recurrence === EventRecurrence.Yearly) text = i18n.t('appointments.recurrences.yearly');

  return text;
}

function getTypeText(categories: string[], context?: 'long' | 'short'): string {
  let text = '';

  if (categories.includes('onSite') && !categories.includes('virtual')) {
    text = i18n.t('events.types.onSite');
  } else if (categories.includes('onSite') && categories.includes('virtual')) {
    if (context && context === 'long') {
      text = i18n.t('events.types.hybrid', { context: 'long' });
    } else if (context && context === 'short') {
      text = i18n.t('events.types.hybrid', { context: 'short' });
    } else {
      text = i18n.t('events.types.hybrid', { context: 'long' });
    }
  } else if (!categories.includes('onSite') && categories.includes('virtual')) {
    if (context && context === 'long') {
      text = i18n.t('events.types.virtual', { context: 'long' });
    } else if (context && context === 'short') {
      text = i18n.t('events.types.virtual', { context: 'short' });
    } else {
      text = i18n.t('events.types.virtual', { context: 'long' });
    }
  }

  return text;
}

function getCategoryText(categories: string[]) {
  let text = '';

  categories.forEach((category) => {
    if (category === 'exam') {
      text = i18n.t('events.categories.exam');
    } else if (category === 'event') {
      text = i18n.t('events.categories.event');
    } else if (category === 'lesson') {
      text = i18n.t('events.categories.lesson');
    }
  });

  return text;
}

async function copyEventLinkToClipboard(uri: string) {
  try {
    await navigator.clipboard.writeText(uri);
    showSuccessToast(i18n.t('common.linkCopied'));
    return Promise.resolve();
  } catch {
    showErrorToast({
      message: i18n.t('meetings.noVirtualLocation'),
      name: '',
      graphQLErrors: [],
    });
    return Promise.reject();
  }
}

async function startEvent(token: string, showError: boolean = true) {
  const resp = await backendApi.startConference(token);
  if (resp.ok) {
    showSuccessToast(i18n.t('meetings.eventDidStart'));
    await Promise.resolve();
  } else if (showError) {
    showErrorToast({
      message: i18n.t('meetings.eventDidNotStart'),
      name: '',
      graphQLErrors: [],
    });
    await Promise.reject();
  }
}

async function joinEvent(token: string, profile: ProfileClaim, asModerator?: boolean): Promise<Response> {
  const joinParams: JoinParams = {
    displayName: profile.displayName ?? '',
    email: profile.email ?? '',
    profileUuid: profile.uuid ?? '',
  };

  if (asModerator !== undefined) {
    joinParams.moderator = asModerator;
  }

  const response = await backendApi.joinConference(token, joinParams);
  const url = await response.text();

  if (url.includes('https://')) {
    if (Capacitor.isNativePlatform()) {
      await Browser.open({ url });
    } else {
      window.open(url, '_blank');
    }
    await Promise.resolve();
  } else {
    showErrorToast({
      message: i18n.t('meetings.noVirtualLocation'),
      name: '',
      graphQLErrors: [],
    });
    await Promise.reject();
  }

  return response;
}

async function startAndJoinEvent(token: string, profile: ProfileClaim, asModerator?: boolean): Promise<Response> {
  await startEvent(token, false);
  return await joinEvent(token, profile, asModerator);
}

async function endEvent(token: string) {
  const resp = await backendApi.endConference(token);
  if (resp.ok) {
    showSuccessToast(i18n.t('meetings.eventDidEnd'));
    Promise.resolve();
  } else {
    showErrorToast({
      message: i18n.t('meetings.eventDidNotEnd'),
      name: '',
      graphQLErrors: [],
    });
    Promise.reject();
  }
}

export {
  getTypeText,
  getCategoryText,
  getRecurrenceEventText,
  copyEventLinkToClipboard,
  startEvent,
  joinEvent,
  startAndJoinEvent,
  endEvent,
};
